/**
 * Hook for loading AI-generated draft for discussion posting
 * Returns loading/error/data states directly for easy consumption
 */

import { useEffect, useState } from 'react';

import { OcChatConfig } from '../../../../../types/oc';
import { draftDiscussionPost } from '../../../services/ocEndpoints';
import { DiscussionPostDraft } from '../../../types/apiTypes';

type LoadableState<T> = 
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'success'; data: T }
  | { status: 'error'; error: Error };

export interface UseOcDraftPostingResult {
  isLoading: boolean;
  isError: boolean;
  data: DiscussionPostDraft | null;
  error: Error | null;
}

/**
 * Hook to load discussion post draft from AI
 * Returns convenient boolean flags and data/error values
 */
export const useOcDraftPosting = (
  config: OcChatConfig,
  sessionId: string,
  enabled: boolean = true,
): UseOcDraftPostingResult => {
  const [state, setState] = useState<LoadableState<DiscussionPostDraft>>({ status: 'idle' });

  useEffect(() => {    
    if (!enabled) {
      return;
    }

    if (!sessionId) {
      setState({
        status: 'error',
        error: new Error('No session ID available. Please start a chat first.'),
      });
      return;
    }

    const loadDraft = async () => {
      setState({ status: 'loading' });
      
      try {
        const data = await draftDiscussionPost(config, { sessionId, aiAssistantKey: config.aiAssistantKey });
        setState({ status: 'success', data });
      } catch (err) {
        setState({
          status: 'error',
          error: err instanceof Error ? err : new Error('Failed to load draft'),
        });
      }
    };

    loadDraft();
  }, [config, sessionId, enabled]);

  // Return convenient values
  return {
    isLoading: state.status === 'loading' || state.status === 'idle',
    isError: state.status === 'error',
    data: state.status === 'success' ? state.data : null,
    error: state.status === 'error' ? state.error : null,
  };
};

